import {
  Component,
  OnDestroy,
  OnInit,
  inject,
  ChangeDetectionStrategy,
  DestroyRef,
  input,
  output,
} from '@angular/core';
import { Apollo } from 'apollo-angular';
import { v4 as uuidv4 } from 'uuid';
import { EventBusService } from '@app/core/services/event-bus/event-bus.service';
import { Subject, zip, Subscription, timer } from 'rxjs';
import { captureException } from '@sentry/angular';
import {
  Filter,
  Report,
  ReportQuery,
  ReportGQL,
  ReportQueryVariables,
  Config,
  RunReportAsyncQueryVariables,
  RunReportAsyncQuery,
  RunReportAsyncGQL,
  FetchReportResultGQL,
  FetchReportResultQuery,
} from '@app/generated/graphql';
import {
  HighChartsCustomConfig,
  ReportConfig,
  Unit,
  RunReportAsyncResponse,
} from '@app/core/interfaces/report.interface';
import { consistentColorPalette } from './ag1-chart-color-palette';
import { filter } from 'rxjs/operators';
// highcharts/highstock includes everything from the core highcharts module plus some additional feature for time series plots (we added it specifically for the horizontal scroll functionality)
import * as Highcharts from 'highcharts/highstock';
import * as HighchartsMore from 'highcharts/highcharts-more';
import { AG1GaugeChartService } from './services/ag1-gauge-chart.service';
import { Ag1PieChartService } from './services/ag1-pie-chart.service';
import { AG1GenericChartService } from './services/ag1-generic-chart.service';
import { TooltipService } from './services/tooltip.service';
import { AutoRefreshService } from './services/auto-refresh.service';
import { PollingService } from '@app/core/services/polling/polling.service';
import { ActivatedRoute } from '@angular/router';
import { Ag1BettabaseErrorComponent } from '../ag1-bettabase-error/ag1-bettabase-error.component';
import { HighchartsChartModule } from 'highcharts-angular';
import { Ag1LoaderComponent } from '../ag1-loader/ag1-loader.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

HighchartsMore.default(Highcharts);

@Component({
  selector: 'ag1-chart',
  templateUrl: './ag1-chart.component.html',
  providers: [Ag1PieChartService, AG1GaugeChartService, AG1GenericChartService],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    Ag1BettabaseErrorComponent,
    HighchartsChartModule,
    Ag1LoaderComponent,
  ],
})
export class Ag1ChartComponent implements OnInit, OnDestroy {
  private _destroyRef = inject(DestroyRef);
  private eventBus = inject(EventBusService);
  private reportGQL = inject(ReportGQL);
  private runReportAsyncGQL = inject(RunReportAsyncGQL);
  private fetchReportResultGQL = inject(FetchReportResultGQL);
  private apollo = inject(Apollo);
  private pieChartService = inject(Ag1PieChartService);
  private gaugeChartService = inject(AG1GaugeChartService);
  private genericChartService = inject(AG1GenericChartService);
  private tooltipService = inject(TooltipService);
  private autoRefreshService = inject(AutoRefreshService);
  private pollingService = inject(PollingService);
  private route = inject(ActivatedRoute);

  chartInstance: Highcharts.Chart | undefined;
  title: string;

  Highcharts: typeof Highcharts = Highcharts;
  defaultChartOptions: Highcharts.Options = {};
  chartOptions: Highcharts.Options = {};
  readonly reportId = input<any>(undefined);
  appliedReportGroupFilters: Filter[];
  reportConfig: ReportConfig;
  reportData: any;
  dataLimit: number = 400;

  // state
  isLoading: boolean = true;
  isError: boolean = false;
  status: string;
  uuid: string;
  rowData: any[];
  // refresh rate in milliseconds
  refreshRate: number | null = null;
  units: Unit[] = [];
  respond_to_switch_unit: boolean = false;

  // Destroy subject for managing subscription cleanup
  private destroy$ = new Subject<void>();

  // stores the subscription for the refresh interval
  private refreshSubscription: Subscription;
  private dataReceived = false; //tracks whether subscription ping on refetchReport has been received

  private readonly TIMEOUT_LIMIT = 30000; // in milliseconds
  private timeoutSubscription: Subscription;

  // error message
  errorName: string;
  errorMessage: string;

  readonly loaded = output<string>();

  private reportGroupCycleId: number = null;

  constructor() {
    // Get cycle ID from query params
    const cycleParam = this.route.snapshot.queryParamMap.get(
      'report_group_cycle_id',
    );
    this.reportGroupCycleId = cycleParam ? parseInt(cycleParam, 10) : null;
  }

  // Capture chart instance with callback
  chartCallback: Highcharts.ChartCallbackFunction = (chart) => {
    this.chartInstance = chart;
  };

  ngOnInit(): void {
    this.initializeDefaultChartOptions();
    this.subscribeToReportGroupFilters();
    this.subscribeToRefetchReport();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.chartInstance = null;
  }

  handleError(error: Error, type?: string): void {
    if (!this.isError) {
      // Clear timeout subscription to prevent timeout state from overriding error state
      this.timeoutSubscription?.unsubscribe();
      console.error(error);

      // Handle error
      this.errorName = error.name || 'An error occurred';
      this.errorMessage = error.message;
      this.isError = true; // slightly confusing naming here. This controlls rendering for both errors and normal communication
      this.isLoading = false;

      this.loaded.emit(this.reportId());

      if (type !== 'communication') {
        captureException(error);
      }
      throw error; // we still throw an 'error' for a communication to stop any further processing
    }
  }

  // clear error state and reset chart
  resetChart(): void {
    this.errorMessage = null;
    this.errorName = null;
    this.chartOptions = this.getDefaultOptions();
    this.isLoading = true;
    this.isError = false;
    this.dataReceived = false;
    this.chartInstance = undefined; // Clear chart instance to force reinitialization
    this.timeoutSubscription?.unsubscribe();
  }

  refetchReport(id: string): void {
    const reportVariables: ReportQueryVariables = {
      reportId: parseInt(this.reportId(), 10),
    };

    // Fetch report and configuration streams
    const configRef$ = this.getConfigStream(reportVariables);
    const dataRef$ = this.getDataStream(id);

    zip(configRef$, dataRef$)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(
        ([configRes, dataRes]) => {
          try {
            this.handleReportSuccess(configRes, dataRes);
          } catch (error) {
            this.handleError(error);
          }
        },
        (error) => {
          this.handleError(error);
        },
      );
  }

  getChartAsync(): void {
    try {
      this.uuid = uuidv4();

      // Start a timeout timer to display a timeout message if no data is received
      this.timeoutSubscription = timer(this.TIMEOUT_LIMIT)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe(() => {
          if (!this.dataReceived) {
            const error = new Error(
              'Please refresh the page or check your connection. If the problem persists, contact support.',
            );
            error.name = 'Chart timed out';
            this.handleError(error, 'communication');
          }
        });

      const reportDataVariables: RunReportAsyncQueryVariables = {
        requestUuid: this.uuid,
        reportId: parseInt(this.reportId(), 10),
        reportFilters: this.appliedReportGroupFilters,
      };
      // runReportGQL stream
      this.apollo
        .query<RunReportAsyncQuery>({
          query: this.runReportAsyncGQL.document,
          variables: reportDataVariables,
          // fetch latest
          fetchPolicy: 'no-cache',
        })
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: ({ data }) => {
            // Start a timeout. If no ping is received, begin polling
            this.waitForSubscriptionPing(data?.runReportAsync);
            this.respond_to_switch_unit =
              !!data?.runReportAsync?.respond_to_switch_unit;
          },
          error: (error) => {
            this.handleError(error);
          },
        });
    } catch (error) {
      this.handleError(error);
    }
  }

  setChartOptions(reportConfigSubset: ReportConfig): void {
    const defaultOptions = this.getDefaultOptions();
    const chartType = this.reportConfig?.chartOptions?.chart?.type;

    switch (chartType) {
      case 'gauge':
        this.setGaugeChartOptions(reportConfigSubset, defaultOptions);
        break;
      case 'pie':
        this.setPieChartOptions(reportConfigSubset, defaultOptions);
        break;
      default:
        this.setGenericChartOptions(reportConfigSubset, defaultOptions);
        break;
    }

    this.updateChartInstance();
    this.isLoading = false;
  }

  getDefaultOptions(): Highcharts.Options {
    return {
      chart: this.getChartOptions(),
      title: this.getTitleOptions(),
      plotOptions: this.getPlotOptions(),
      yAxis: this.getYAxisOptions(),
      colors: consistentColorPalette,
      credits: this.getCreditsOptions(),
      tooltip: this.getTooltipOptions(),
    };
  }

  ///////////////////////////////////////////////////////////////////////////////////////

  //////////////////////////////////////////////////////
  // #region       ngOnInit  helpers                  //
  //////////////////////////////////////////////////////
  private initializeDefaultChartOptions(): void {
    this.defaultChartOptions = this.getDefaultOptions();
  }

  private subscribeToReportGroupFilters(): void {
    this.eventBus
      .getChannel('appliedReportGroupFilters')
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((filters) => Array.isArray(filters)),
      )
      .subscribe((appliedReportGroupFilters) => {
        this.resetChart();
        this.appliedReportGroupFilters = appliedReportGroupFilters;
        this.getChartAsync();
      });
  }

  private subscribeToRefetchReport(): void {
    this.eventBus
      .getChannel('RefetchReport')
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((data: { id: string; status: string }) => !!data?.id),
      )
      .subscribe((data: { id: string; status: string }) => {
        if (this.uuid === data.id) {
          if (data.status === 'failed') {
            const error = new Error(
              'Please refresh the page or check your connection. If the problem persists, contact support.',
            );
            error.name = 'Report Subscription Error';
            this.handleError(error);
          } else {
            this.refetchReport(data.id);
          }
        }
      });
  }

  private subscribeToUnitChanges(): void {
    this.eventBus
      .getChannel('unitChange')
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((unit) => Object.keys(unit).length > 0),
      ) // Ignore initial or invalid values)
      .subscribe((unit) => {
        if (unit) {
          this.onUnitChange(unit);
        }
      });
  }

  private onUnitChange(selectedUnit: Unit) {
    const reportConfigSubset = structuredClone(this.reportConfig);

    // Make substitutions if available for chart ttle
    const placeholder = '${unit}';
    const title = this.reportConfig?.chartOptions?.title?.text.replace(
      placeholder,
      selectedUnit.label,
    );
    reportConfigSubset.chartOptions.title.text = title;

    // Keep only y-axes associated with the selected unit
    const selectedYAxis = selectedUnit.value;
    if (Array.isArray(reportConfigSubset.chartOptions.yAxis)) {
      reportConfigSubset.chartOptions.yAxis =
        reportConfigSubset.chartOptions.yAxis.filter(
          (yAxis: Highcharts.YAxisOptions) => yAxis.id === selectedYAxis,
        );
    } else {
      reportConfigSubset.chartOptions.yAxis =
        reportConfigSubset.chartOptions.yAxis.id == selectedYAxis
          ? reportConfigSubset.chartOptions.yAxis
          : null;
    }
    // trigger the refresh
    this.setChartOptions(reportConfigSubset);
  }

  ////////////////////////////////////////////////////////////////////////////////

  //////////////////////////////////////////////////////
  // #region      refetchReport  helpers              //
  //////////////////////////////////////////////////////
  private getConfigStream(variables: ReportQueryVariables) {
    return this.apollo.query<ReportQuery>({
      query: this.reportGQL.document,
      variables,
      fetchPolicy: 'cache-first',
    });
  }

  private getDataStream(id: string) {
    return this.apollo.query<FetchReportResultQuery>({
      query: this.fetchReportResultGQL.document,
      variables: { requestUuid: id },
      fetchPolicy: 'no-cache',
    });
  }

  private handleReportSuccess(
    configRes: { data: ReportQuery },
    dataRes: { data: FetchReportResultQuery },
  ): void {
    try {
      const report: Report = configRes.data.report;
      const config: Config[] = structuredClone(report.configs);
      const reportData = dataRes.data.fetchReportResult;

      this.reportConfig = this.getReportConfig(config);
      this.dataReceived = this.isDataReceived(reportData);
      this.title = this.reportConfig.chartOptions?.title?.text;

      if (
        !this.refreshSubscription &&
        this.reportConfig?.highChartsCustom?.autoRefresh?.enabled &&
        !this.reportGroupCycleId
      ) {
        this.setupAutoRefresh();
      }

      if (this.dataReceived) {
        // Clear timeout subscription
        this.timeoutSubscription?.unsubscribe();

        this.processReportData(reportData);
        this.setChartOptions(this.reportConfig);
        if (this.respond_to_switch_unit) {
          this.subscribeToUnitChanges(); // want this to trigger after dataReceived
        }

        this.loaded.emit(this.reportId());
      }
    } catch (error) {
      this.handleError(error);
    }
  }

  // Extract report configurations
  private getReportConfig(config: Config[]): ReportConfig {
    return config.reduce((obj, item) => {
      obj[item.name] = this.parseJSON(item.value, item.name);
      return obj;
    }, {});
  }

  // Utility function to parse JSON safely
  private parseJSON(value: string, name: string) {
    try {
      return JSON.parse(value);
    } catch {
      this.handleError(new Error(`Invalid JSON value for ${name}`));
    }
  }

  private isDataReceived(reportData: any): boolean {
    return reportData !== 'null';
  }

  private setupAutoRefresh(): void {
    const highchartsCustomConfig: HighChartsCustomConfig =
      this.reportConfig.highChartsCustom;
    const refreshRate = highchartsCustomConfig.autoRefresh.refreshRate || 300;
    this.refreshRate = refreshRate * 1000;

    this.refreshSubscription = this.autoRefreshService.setupAutoRefresh(
      this.refreshRate,
      this.destroy$,
      () => this.getChartAsync(),
    );
  }

  private processReportData(reportData: any): void {
    const parsedReportData =
      typeof reportData === 'string' ? JSON.parse(reportData) : reportData;
    if (parsedReportData?.result.length > 0) {
      this.reportData = parsedReportData?.result;
    } else {
      const error = new Error(
        'Try adjusting your filters to broaden the search.',
      );
      error.name = 'No data to display';

      this.handleError(error, 'communication');
    }

    // to prevent ram issues. Adjust dataLimit if needed
    if (this.reportData.length > this.dataLimit) {
      const error = new Error(
        'Try adjusting your filters to narrow the search.',
      );
      error.name = 'Too much data to display (RAM risk)';

      this.handleError(error, 'communication');
    }
  }
  /////////////////////////////////////////////////////////////////////////////

  //////////////////////////////////////////////////////
  // #region       getChartAsync helpers              //
  //////////////////////////////////////////////////////
  private waitForSubscriptionPing(configs: RunReportAsyncResponse): void {
    // Start polling after an initial delay to give the
    // subscription callback a chance to run
    timer(200)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        if (!this.dataReceived) {
          this.startPolling();
        }
      });
  }

  private startPolling(): void {
    this.pollingService.startPolling(
      // Poll function
      () => this.refetchReport(this.uuid),
      // Stop condition
      () => this.dataReceived,
      // Destroy subject
      this.destroy$,
    );
  }

  /////////////////////////////////////////////////////////////////////////////

  //////////////////////////////////////////////////////
  // #region       setChartOptions helpers            //
  //////////////////////////////////////////////////////

  private setGaugeChartOptions(
    reportConfigSubset: ReportConfig,
    defaultOptions: any,
  ): void {
    this.chartOptions = this.gaugeChartService.getOptions(
      reportConfigSubset,
      this.reportData,
      defaultOptions,
    );
  }

  private setPieChartOptions(
    reportConfigSubset: ReportConfig,
    defaultOptions: any,
  ): void {
    this.chartOptions = this.pieChartService.getOptions(
      reportConfigSubset,
      this.reportData,
      defaultOptions,
    );
  }

  private setGenericChartOptions(
    reportConfigSubset: ReportConfig,
    defaultOptions: any,
  ): void {
    this.chartOptions = this.genericChartService.getOptions(
      reportConfigSubset,
      this.reportData,
      defaultOptions,
    );
  }

  private updateChartInstance(): void {
    if (this.chartInstance && this.chartOptions) {
      this.chartInstance.update(this.chartOptions, true, true);
    }
  }

  ////////////////////////////////////////////////////////////////////////////////

  //////////////////////////////////////////////////////
  // #region      getDefaultOptions  helpers          //
  //////////////////////////////////////////////////////
  private getChartOptions(): Highcharts.ChartOptions {
    return {
      backgroundColor: '#ffffff',
      style: {
        fontFamily: 'DM Sans',
      },
    };
  }

  private getTitleOptions(): Highcharts.TitleOptions {
    return {
      useHTML: true,
      style: {
        color: '#4B5563',
        fontSize: '1rem',
        lineHeight: '1.5rem',
        fontWeight: '600',
        wordWrap: 'break-word',
      },
    };
  }

  private getPlotOptions(): Highcharts.PlotOptions {
    return {
      bar: {
        stacking: 'normal',
        borderRadius: 5,
        minPointLength: 3, // Ensures small values are visible by setting a min height on bars
        dataLabels: {
          enabled: false, // Disable individual data labels for stacked bar series (show totals only)
        },
      },
      series: {
        dataLabels: {
          enabled: true,
          style: {
            color: '#1E293B',
            fontWeight: 'normal',
          },
        },
      },
    };
  }

  private getYAxisOptions(): Highcharts.YAxisOptions {
    return {
      stackLabels: {
        enabled: true,
        style: {
          color: '#1E293B',
          fontWeight: 'normal',
        },
      },
    };
  }

  private getCreditsOptions(): Highcharts.CreditsOptions {
    return {
      enabled: false,
    };
  }

  private getTooltipOptions(): Highcharts.TooltipOptions {
    return {
      useHTML: true,
      formatter: this.tooltipService.getTooltipFormatter(),
      backgroundColor: '#212B36',
      borderColor: '#000000',
      style: {
        color: '#FFFFFF', // Text color
      },
      borderRadius: 8,
      borderWidth: 1,
      padding: 10,
      shared: false,
      outside: true, // Allow tooltip to be rendered outside the chart area
    };
  }
  ////////////////////////////////////////////////////////////////////////////////
}
